import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { ExclamationCircleFilled } from '@ant-design/icons';
import ImageService from 'services/image';
import FolderService from 'services/folder';
import { useApp } from 'contexts/app';
import ImagePickerContext from '../ImagePickerContext';

const ROOT_FOLDER_ID = 'root';
const ROOT_FOLDER_NAME = 'Home';
const PAGE_SIZE = 40;

const useComponent = ({ open, onOk, onCancel }) => {
  const { folder, setFolder, value, maxPhotos, onChange } = useContext(ImagePickerContext);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [folderList, setFolderList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [folderPickerOnSelectFn, setFolderPickerOnSelectFn] = useState({ callback: async () => {} });
  const fileInputRef = useRef(null);
  const newFolderNameInputRef = useRef(null);
  const initializedOnOpenRef = useRef(false);
  const latestLoadRequestRef = useRef(0);
  const { showNotify, modal, showLoading, prompt } = useApp();

  const loadFolder = async () => {
    const service = new FolderService();
    const list = await service.list();
    setFolderList(list || []);
  };

  const loadData = async (folderLoad) => {
    const requestId = latestLoadRequestRef.current + 1;
    latestLoadRequestRef.current = requestId;
    setFiles([]);

    const imageService = new ImageService();
    const list = await imageService.list(folderLoad);

    if (requestId !== latestLoadRequestRef.current) {
      return;
    }

    setFiles((list || []).map((item) => ({ ...item, selected: false })));
    setCurrentPage(1);
  };

  const delayLoad = useMemo(() => _.debounce(loadData, 500), []);

  useEffect(() => {
    if (open && !initializedOnOpenRef.current) {
      initializedOnOpenRef.current = true;
      setCurrentPage(1);
      setFiles([]);

      if (folder !== ROOT_FOLDER_ID) {
        setFolder(ROOT_FOLDER_ID);
        return;
      }
    }

    if (open) {
      delayLoad(folder || ROOT_FOLDER_ID);
    } else {
      delayLoad.cancel();
      initializedOnOpenRef.current = false;
      setFiles([]);
    }
  }, [delayLoad, folder, open, setFolder]);

  useEffect(() => () => delayLoad.cancel(), [delayLoad]);
  useEffect(() => {
    loadFolder();
  }, []);

  const pagedFiles = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return files.slice(start, end);
  }, [currentPage, files]);

  const selectedFiles = useMemo(() => files.filter((item) => item.selected), [files]);
  const selectedCount = selectedFiles.length;
  const existingCount = Array.isArray(value) ? value.length : 0;
  const remainingSlots = Math.max(maxPhotos - existingCount, 0);

  const currentFolder = useMemo(() => folderList.find((item) => item.id == folder), [folderList, folder]);
  const childFolders = useMemo(() => folderList.filter((item) => item.parent_id == folder), [folderList, folder]);

  const breadcrumbFolders = useMemo(() => {
    if (folder === ROOT_FOLDER_ID) {
      return [{ id: ROOT_FOLDER_ID, name: ROOT_FOLDER_NAME }];
    }

    const chain = [];
    let cursor = currentFolder;

    while (cursor) {
      chain.unshift(cursor);
      if (!cursor.parent_id || cursor.parent_id === ROOT_FOLDER_ID) {
        break;
      }
      cursor = folderList.find((item) => item.id == cursor.parent_id);
    }

    return [{ id: ROOT_FOLDER_ID, name: ROOT_FOLDER_NAME }, ...chain];
  }, [currentFolder, folder, folderList]);

  const bulkActionItems = [
    { label: 'Xóa', key: 'delete_images' },
    { label: 'Di chuyển', key: 'move_images' },
  ];

  const folderActions = [
    { label: 'Đổi tên', key: 'rename_folder' },
    { label: 'Di chuyển', key: 'move_folder' },
    { label: 'Xóa', key: 'delete_folder' },
  ];

  const openFolder = (folderId) => setFolder(folderId);

  const clearSelection = () => {
    setFiles((prev) => prev.map((item) => ({ ...item, selected: false })));
  };

  const toggleImageSelected = (image) => {
    setFiles((prev) => {
      const index = prev.findIndex((item) => item.id === image.id);
      if (index === -1) {
        return prev;
      }

      const next = [...prev];
      const target = next[index];
      const currentSelectedCount = prev.filter((item) => item.selected).length;

      if (!target.selected && currentSelectedCount >= remainingSlots) {
        showNotify(`Chỉ có thể chọn thêm ${remainingSlots} ảnh`);
        return prev;
      }

      next[index] = { ...target, selected: !target.selected };
      return next;
    });
  };

  const confirmSelection = () => {
    const selectedList = selectedFiles.map((item) => {
      const nextItem = { ...item };
      delete nextItem.selected;
      return nextItem;
    });

    onChange(_.concat(value ?? [], selectedList));
    if (onOk) {
      onOk();
    } else if (onCancel) {
      onCancel();
    }
  };

  const uploadImage = async (file) => {
    const imageService = new ImageService();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);
    return imageService.upload(formData);
  };

  const onFileChange = async (event) => {
    setUploading(true);

    try {
      for (let index = 0; index < event.target.files.length; index += 1) {
        await uploadImage(event.target.files[index]);
      }

      await loadData(folder);
      showNotify('Tải ảnh thành công');
    } catch (error) {
      showNotify('Tải ảnh thất bại, vui lòng thử lại');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const deleteImages = async () => {
    modal.confirm({
      title: 'Bạn có chắc chắn muốn xoá các hình này?',
      icon: <ExclamationCircleFilled />,
      onOk: async () => {
        const ids = selectedFiles.map((item) => item.id);
        const imageService = new ImageService();
        showLoading(true);
        await imageService.deleteList(ids);
        await loadData(folder);
        showLoading(false);
      },
    });
  };

  const moveImages = () => {
    setShowFolderPicker(true);
    setFolderPickerOnSelectFn({
      callback: async (nextFolderId) => {
        const ids = selectedFiles.map((item) => item.id);
        const imageService = new ImageService();
        showLoading(true);
        await imageService.moveList(ids, nextFolderId);
        await loadData(folder);
        showLoading(false);
      },
    });
  };

  const onBulkActionClick = ({ key }) => {
    if (key === 'delete_images') {
      deleteImages();
    }

    if (key === 'move_images') {
      moveImages();
    }
  };

  const createFolder = async () => {
    const name = newFolderNameInputRef.current?.input?.value?.trim();
    if (!name) {
      showNotify('Vui lòng nhập tên thư mục');
      return;
    }

    const service = new FolderService();
    const createdFolder = await service.create({ name, parent_id: folder });
    showNotify(`Đã tạo folder ${name}`);
    setShowCreateFolder(false);
    await loadFolder();
    setFolder(createdFolder.id);
  };

  const renameFolder = () => {
    prompt({
      title: 'Đổi tên',
      label: 'Nhập tên',
      onOk: async (text) => {
        const service = new FolderService();
        await service.update({ id: folder, name: text });
        await loadFolder();
      },
    });
  };

  const deleteFolder = () => {
    modal.confirm({
      title: 'Bạn có chắc chắn muốn xoá folder?',
      icon: <ExclamationCircleFilled />,
      onOk: async () => {
        const selectedFolder = folderList.find((item) => item.id == folder);
        if (!selectedFolder) {
          return;
        }

        const service = new FolderService();
        await service.delete(folder);
        await loadFolder();
        setFolder(selectedFolder.parent_id || ROOT_FOLDER_ID);
      },
    });
  };

  const moveFolder = () => {
    setShowFolderPicker(true);
    setFolderPickerOnSelectFn({
      callback: async (nextFolderId) => {
        showLoading(true);
        const service = new FolderService();
        await service.update({ id: folder, parent_id: nextFolderId });
        await loadFolder();
        showLoading(false);
      },
    });
  };

  const onFolderActionClick = ({ key }) => {
    if (key === 'rename_folder') {
      renameFolder();
    }

    if (key === 'move_folder') {
      moveFolder();
    }

    if (key === 'delete_folder') {
      deleteFolder();
    }
  };

  const handleFolderPickerSelected = async (nextFolderId) => {
    await folderPickerOnSelectFn.callback(nextFolderId);
    setShowFolderPicker(false);
  };

  return {
    bulkActionItems,
    breadcrumbFolders,
    childFolders,
    clearSelection,
    confirmSelection,
    createFolder,
    currentFolder,
    currentPage,
    fileInputRef,
    files,
    folderActions,
    handleFolderPickerSelected,
    maxPhotos,
    newFolderNameInputRef,
    onBulkActionClick,
    onCancel,
    onFileChange,
    onFolderActionClick,
    open,
    openFolder,
    pagedFiles,
    remainingSlots,
    selectedCount,
    setCurrentPage,
    setShowCreateFolder,
    setShowFolderPicker,
    showCreateFolder,
    showFolderPicker,
    toggleImageSelected,
    totalFiles: files.length,
    uploading,
  };
};

export default useComponent;
